import { useEffect } from 'react';

import { Button, H1, H2, Icon, IconName, ListRow, Text, VStack } from '@betterment/samba-react';
import { SambaAutoLink, WorkflowLayout } from '@betterment/shared-components';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { urlBuilder } from '../../../Shared/urlBuilder';
import { PurposeType, usePurposeSelectionStep } from '../FlowConfig';

type Selection = {
  type: PurposeType;
  title: string;
  description: string;
  icon: IconName;
};

const SELECTIONS: Selection[] = [
  {
    type: 'emergency_fund',
    title: 'Emergency fund',
    description: 'We’ll help you figure out the right amount to save based on your current expenses.',
    icon: 'SafetyNet',
  },
  {
    type: 'retirement',
    title: 'Retirement',
    description:
      'We’ll help you create a plan so you can retire when you want with enough money for the life you want.',
    icon: 'BeachUmbrella',
  },
  {
    type: 'major_purchase',
    title: 'Specific goal',
    description:
      'Save for a specific amount, target date, or goal like building wealth, a home, vacation, education, etc.',
    icon: 'Ribbon',
  },
];

const VALID_PRODUCT_CATEGORIES_WITH_PURPOSE = [
  'investing',
  'cash',
  'bond_investing',
  'retirement',
  'general_investing',
] as const;
type ProductCategory = (typeof VALID_PRODUCT_CATEGORIES_WITH_PURPOSE)[number];

const isValidProductCategory = (productCategory?: string): productCategory is ProductCategory => {
  return VALID_PRODUCT_CATEGORIES_WITH_PURPOSE.includes(productCategory as ProductCategory);
};

export const SelectionStep: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productCategory = searchParams.get('product_category')?.toLowerCase();
  const { commit } = usePurposeSelectionStep('selection');

  const hasValidProductCategory = isValidProductCategory(productCategory);

  useEffect(() => {
    if (productCategory === 'retirement') {
      commit({ purposeType: productCategory });
      void navigate('/purpose_selection/assignment', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasValidProductCategory) {
    throw new Error(`Invalid product category provided, ${productCategory}.`);
  }

  const handleNextStep = (purposeType: PurposeType) => {
    commit({ purposeType });
    void navigate('/purpose_selection/assignment');
  };

  return (
    <WorkflowLayout pageTitle="Set your investing target">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => navigate('/')} />
      <WorkflowLayout.Content>
        <VStack css={{ gap: 'space.300' }}>
          <VStack css={{ gap: 'space.200' }}>
            <H1 css={{ textWrap: 'auto' }}>Do you want to set a goal for this account?</H1>
            <Text color="text.weak">We can recommend a savings plan to help you achieve it.</Text>
          </VStack>
          <VStack css={{ gap: 'space.150' }}>
            {SELECTIONS.map(({ type, title, description, icon }) => (
              <Button
                key={type}
                onClick={() => handleNextStep(type)}
                variant="ghostSecondary"
                spacing="compact"
                css={{
                  borderStyle: 'solid',
                  borderWidth: 'thin',
                  borderColor: 'border.weak',
                  borderRadius: 'medium',
                  py: '0',
                  px: '0',
                  overflow: 'hidden',
                }}
              >
                <ListRow css={{ cursor: 'pointer' }}>
                  <ListRow.LeadingAsset css={{ alignSelf: 'start' }}>
                    <Icon name={icon} size="small" />
                  </ListRow.LeadingAsset>
                  <ListRow.LeadingContent>
                    <VStack css={{ display: 'grid' }}>
                      <H2 variant="title100">{title}</H2>
                      <Text variant="text50" color="text.weak">
                        {description}
                      </Text>
                    </VStack>
                  </ListRow.LeadingContent>
                  <ListRow.TrailingAsset>
                    <Icon name="CaretRight" size="small" />
                  </ListRow.TrailingAsset>
                </ListRow>
              </Button>
            ))}
          </VStack>
          <SambaAutoLink
            to={urlBuilder.appAddGoalFlowsNew.absolute({
              advice_type: 'build_wealth',
              product_category: productCategory,
            })}
            variant="solidPrimary"
            css={{ width: 'fit-content' }}
          >
            Skip for now
          </SambaAutoLink>
        </VStack>
      </WorkflowLayout.Content>
    </WorkflowLayout>
  );
};
